import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "components/ui/button";
import routes from "routes/routes";
import { RootState } from "../../../redux/store";
import { withReturnTo } from "../common/returnTo";

/**
 * "Already have an account? Log in" — offered on the customer-data step
 * (#1926).
 *
 * Deliberately an offer, never a gate: guest checkout stays an equal path
 * (ADR-0008 Layer 3 / plan §1), so this renders as one line above the form and
 * nothing below it changes. Logging in prefills the form from the account
 * profile (`usePersonalDataPersistence`) and, on tenants with member coverage,
 * is what unlocks the covered booking outcome.
 *
 * Renders only when the tenant has accounts enabled and nobody is logged in —
 * a logged-in user has nothing to gain from it, and on a tenant without
 * accounts the route does not even exist.
 *
 * `returnTo` carries the current location so the login flow lands the user
 * back in the booking step they left, rather than on the account page.
 */
const AccountLoginPrompt: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const accountsEnabled = useSelector(
    (state: RootState) => state.tenantConfig.config.accountsEnabled === true
  );
  const isLoggedIn = useSelector(
    (state: RootState) => state.account?.profile != null
  );

  if (!accountsEnabled || isLoggedIn) return null;

  const returnTo = `${location.pathname}${location.search}`;

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[5px] bg-slate-50 px-3 py-2">
      <span className="text-sm text-slate-600">
        {t("account.login_prompt.question")}
      </span>
      <Button
        type="button"
        variant="link"
        className="h-auto p-0 text-sm font-medium"
        onClick={() =>
          navigate(withReturnTo(routes.accountLoginPath, returnTo))
        }
      >
        {t("account.login_prompt.action")}
      </Button>
    </div>
  );
};

export default AccountLoginPrompt;
